import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';

import { Button, CenteredModal, useToast } from 'components';
import { useDispatch } from 'react-redux';
import { deletePanel, refreshCurrentClient, Panel, ThunkDispatch } from 'store';
import { ApiFormattedException } from '../../../../store/modules/exceptions';

const useStyles = createUseStyles((theme: Theme) => ({
  buttons: {
    textAlign: 'right',
  },
  lastButton: {
    marginLeft: theme.marginBase,
  },
  content: {
    paddingBottom: theme.marginBase * 4,
    paddingTop: theme.marginBase * 4,
  },
}));

interface Props {
  onRequestClose: () => void;
  isOpen: boolean;
  panel: Partial<Panel> | null;
}

export const DeletePanelModal = ({ panel, isOpen, onRequestClose }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const dispatch = useDispatch<ThunkDispatch>();

  const toast = useToast();
  const { t } = useTranslation();

  if (!panel) {
    return null;
  }

  const panelId = panel.id;

  const handleDelete = async () => {
    try {
      if (!panelId) {
        return;
      }
      await dispatch(deletePanel(panelId));
      toast.saved();
      onRequestClose();
      dispatch(refreshCurrentClient());
    } catch (e) {
      if (e instanceof ApiFormattedException && e.getTranslatedMessage()) {
        toast.error(e.getTranslatedMessage());
      } else {
        toast.exception();
      }
    }
  };

  return (
    <CenteredModal
      onRequestClose={onRequestClose}
      isOpen={isOpen}
      title={t('settings.panels.deleteModal.title', { name: panel.name })}
    >
      <div className={classes.content}>
        <Trans i18nKey="settings.panels.deleteModal.message" />
      </div>
      <div className={classes.buttons}>
        <Button
          text={t('settings.panels.deleteModal.cancelButton')}
          onClick={onRequestClose}
          small
          color="ink"
          variant="transparent"
        />
        <Button
          text={t('settings.panels.deleteModal.submitButton')}
          color="red"
          className={classes.lastButton}
          onClick={handleDelete}
          small
        />
      </div>
    </CenteredModal>
  );
};
